import { useEffect, useState } from "react";
import { Twitter, Facebook, Linkedin, Mail, MessageCircle, Send, Link as LinkIcon, Check } from "lucide-react";
import { api } from "@/lib/api";
import { loadIconStyleSettings, iconWrapperProps } from "@/lib/iconStyles";

// Everything ShareBar knows how to render.
// key must match backend SHARE_PLATFORMS.
export const SHARE_PLATFORM_META = {
  twitter:  { label: "Share on X / Twitter", Icon: Twitter,     build: (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
  facebook: { label: "Share on Facebook",    Icon: Facebook,    build: (u)    => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
  linkedin: { label: "Share on LinkedIn",    Icon: Linkedin,    build: (u)    => `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
  reddit:   { label: "Share on Reddit",      Icon: RedditIcon,  build: (u, t) => `https://reddit.com/submit?url=${u}&title=${t}` },
  email:    { label: "Share via email",      Icon: Mail,        build: (u, t) => `mailto:?subject=${t}&body=${u}` },
  whatsapp: { label: "Share on WhatsApp",    Icon: MessageCircle, build: (u, t) => `https://api.whatsapp.com/send?text=${t}%20${u}` },
  telegram: { label: "Share on Telegram",    Icon: Send,        build: (u, t) => `https://t.me/share/url?url=${u}&text=${t}` },
};

let _settingsCache = null;
let _settingsPromise = null;
async function loadShareSettings() {
  if (_settingsCache) return _settingsCache;
  if (!_settingsPromise) {
    _settingsPromise = api.get("/site-settings/share")
      .then(r => { _settingsCache = r.data; return r.data; })
      .catch(() => ({ platforms: [], copy_link_enabled: true }));
  }
  return _settingsPromise;
}
// Exposed for the admin panel to invalidate after saving.
export function invalidateShareSettingsCache() { _settingsCache = null; _settingsPromise = null; }

/**
 * Monochrome share bar for a blog page.
 * Reads admin-configured platform enable/order from /api/site-settings/share.
 */
export default function ShareBar({ title, url, className = "" }) {
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState(null);
  const [iconStyle, setIconStyle] = useState({ style: "monochrome", size: "md" });

  useEffect(() => { loadShareSettings().then(setSettings); }, []);
  useEffect(() => { loadIconStyleSettings().then(setIconStyle); }, []);

  const t = encodeURIComponent(title || "");
  const u = encodeURIComponent(url || "");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  if (!settings) return null;
  const enabledPlatforms = (settings.platforms || []).filter(p => p.enabled && SHARE_PLATFORM_META[p.key]);
  const showCopy = settings.copy_link_enabled !== false;
  if (enabledPlatforms.length === 0 && !showCopy) return null;

  return (
    <div data-testid="blog-share-bar" className={`flex items-center gap-1 ${className}`}>
      <span className="text-xs uppercase tracking-[0.2em] text-slate-500 mr-2">Share</span>
      {enabledPlatforms.map(({ key }) => {
        const { label, Icon, build } = SHARE_PLATFORM_META[key];
        const wrap = iconWrapperProps({ platformKey: key, styleName: iconStyle.style, sizeName: iconStyle.size });
        return (
          <a key={key} href={build(u, t)} target="_blank" rel="noopener noreferrer"
             data-testid={`blog-share-${key}`}
             aria-label={label} title={label}
             className={wrap.className}
             style={wrap.style}>
            <Icon className={wrap.iconSizeClass}/>
          </a>
        );
      })}
      {showCopy && (() => {
        const wrap = iconWrapperProps({ platformKey: "email", styleName: iconStyle.style, sizeName: iconStyle.size });
        return (
          <button data-testid="blog-share-copy" onClick={copyLink} aria-label="Copy link"
                  title={copied ? "Copied!" : "Copy link"}
                  className={wrap.className}
                  style={wrap.style}>
            {copied ? <Check className={`${wrap.iconSizeClass} text-emerald-600`}/> : <LinkIcon className={wrap.iconSizeClass}/>}
          </button>
        );
      })()}
    </div>
  );
}

// Lucide doesn't ship a reddit icon, so draw a compact inline one at 16x16.
function RedditIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" className={className} fill="currentColor" aria-hidden="true">
      <path d="M22 12.5a2.5 2.5 0 0 0-4.35-1.68c-1.36-.86-3.24-1.4-5.29-1.47l1.09-4.4 3.83.85a1.6 1.6 0 1 0 .27-1.24l-4.31-.96a.5.5 0 0 0-.6.37l-1.28 5.17c-2.11.06-4.05.6-5.44 1.48A2.5 2.5 0 1 0 3.7 14.7c-.01.13-.02.26-.02.4C3.68 18.53 7.4 21 12 21s8.32-2.47 8.32-5.9c0-.14 0-.27-.02-.4A2.5 2.5 0 0 0 22 12.5ZM8.75 14a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0Zm7.66 3.11c-.98.97-2.66 1.45-4.41 1.45s-3.43-.48-4.41-1.45a.6.6 0 0 1 .85-.85c.7.7 2.05 1.1 3.56 1.1s2.86-.4 3.56-1.1a.6.6 0 0 1 .85.85ZM15 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Z"/>
    </svg>
  );
}
